<template>
  <VerticalContainer narrow-single-button>
    <div class="temperature-data-download">
      <div class="header-container">
        <Title
          title="Temperature Data Download"
          is-full-width
          is-centered
        />
        <div class="summary-container">
          <div>
            <div class="item">
              <div class="item-title">
                Labels detected
              </div>
              <div class="item-value">
                {{ detectedLabelsCount }}
              </div>
            </div>
            <div class="item">
              <div class="item-title">
                Downloaded
              </div>
              <div class="item-value">
                {{ downloadedLabelsCount }}
              </div>
            </div>
          </div>
          <div>
            <div class="item">
              <div class="item-title">
                Status
              </div>
              <div class="item-value">
                {{ status }}
              </div>
            </div>
            <div class="item">
              <div
                class="item-title"
                :class="{'highlight': !isInProgress && anyIncomplete}"
              >
                {{ statusMessage }}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="content-container">
        <div
          v-if="isInitialLoading"
          class="loader-wrapper"
        >
          <Loader is-loading />
        </div>
        <div
          v-if="message"
          class="message-container"
          :class="{
            'is-loading': isInitialLoading,
          }"
        >
          {{ message }}
        </div>
        <Loader
          color="blue"
          is-linear
          :is-loading="isInProgress"
        />
        <div class="labels-container">
          <TLabelItem
            v-for="(label, index) in discoveredTLabelAssets"
            :key="`t-label-${index}-${labelsStatusesWatchId}-${isInProgress}`"
            v-bind="label"
            :download-status="$store.state.mobileApp.tLabels
              .downloadStatuses[label.bleId]?.status || null"
            :retry-is-disabled="isInProgress"
            @retry="retryLabelDownload(label.bleId)"
          />
        </div>
        <div
          v-if="cleanedUpLabelsMessage"
          class="cleanup-message"
        >
          {{ cleanedUpLabelsMessage }}
        </div>
      </div>
    </div>
    <template #buttons>
      <Button
        v-if="actionButton"
        :text="actionButton.text"
        :is-text="actionButton.isText || false"
        :with-outline="actionButton.isText || false"
        has-padding
        @click="actionButton.action"
      />
    </template>
    <ButtonsDialogue
      :is-visible="displayCancelDialog"
      :is-loading="isCancellingDownload"
      title="Are you sure you?"
      message="This will stop the download process for any labels that are not complete."
      @yes="cancelDownload"
      @no="closeCancelDialog"
    />
  </VerticalContainer>
</template>
<script>
import { routeNames } from '../../constants';
import VerticalContainer from '../../components/organisms/layout/vertical-container';
import Title from '../../components/atoms/table-title/table-title';
import Button from '../../components/atoms/button/button';
import Loader from '../../components/atoms/loader/loader';
import TLabelItem from '../../components/molecules/temperature/t-label-item';
import ButtonsDialogue from '../../components/molecules/dialogues/yes-no-dialogue';

import updateDateTimes from '../../mixins/update-date-times';
import dateTimeHelper from '../../mixins/date-time-helper';

const tLabelInitialScanDurationSecs = process.env
  .VUE_APP_T_LABEL_INITIAL_SCAN_DURATION_SECS || 60;

export default {
  name: 'TemperatureDataDownload',
  components: {
    VerticalContainer,
    Title,
    Button,
    Loader,
    TLabelItem,
    ButtonsDialogue,
  },
  mixins: [updateDateTimes, dateTimeHelper],
  data() {
    return {
      displayCancelDialog: false,
      isCancellingDownload: false,
    };
  },
  computed: {
    hasPermission() { return !this.$root.isViewer; },
    isMobileGatewayActive() {
      return this.$store.state.mobileApp.isMobileGatewayActive;
    },
    isInitialLoading() {
      if (!this.isMobileGatewayActive) {
        return false;
      }

      if (this.detectedLabelsCount > 0) {
        return false;
      }

      const initialScanDuration = this.$store.state.mobileApp.mobileGatewayActiveAt
        ? Date.now() - this.$store.state.mobileApp.mobileGatewayActiveAt.getTime()
        : null;

      if (initialScanDuration >= tLabelInitialScanDurationSecs * 1000) {
        return false;
      }

      // to ensure this gets updated - use the timer
      // - use it at the end, so that it doesn't get re-evaluated after the initial scan duration
      return this.timer >= 0 || true;
    },
    discoveredTLabelAssets() { return this.$store.getters['mobileApp/tLabels/discoveredTLabelAssets']; },
    detectedLabelsCount() { return this.discoveredTLabelAssets.length; },
    downloadedLabelsCount() { return this.$store.getters['mobileApp/tLabels/downloadedLabelsCount']; },
    failureCount() { return this.$store.getters['mobileApp/tLabels/failureCount']; },
    message() {
      if (!this.hasPermission) {
        return 'You do not have permission to download temperature data';
      }

      if (!this.isMobileGatewayActive && this.detectedLabelsCount === 0) {
        return 'Please start the mobile gateway to download temperature data';
      }

      if (this.isInitialLoading) {
        return 'Scanning for temperature labels...';
      }

      if (this.detectedLabelsCount === 0) {
        return 'No temperature labels detected';
      }

      return '';
    },
    startedAt() { return this.$store.state.mobileApp.tLabels.bulkDownloadStartedAt; },
    isInProgress() { return this.$store.getters['mobileApp/tLabels/bulkDownloadInProgress']; },
    allComplete() {
      return !this.isInProgress // use inProgress to force reactivity
        && !this.anyIncomplete;
    },
    anyIncomplete() {
      return this.detectedLabelsCount > this.downloadedLabelsCount;
    },
    status() {
      if (this.detectedLabelsCount === 0) {
        return 'No labels detected';
      }

      if (this.allComplete) {
        return 'Complete';
      }

      if (this.isInProgress) {
        return 'Running';
      }

      return 'Not started';
    },
    statusMessage() {
      if (this.isInProgress && this.timer >= 0) {
        // include timer to force re-evaluation every 10 seconds
        return this.getTimeAgo(this.startedAt.toISOString());
      }

      if (this.anyIncomplete) {
        return 'Data available';
      }

      if (this.failureCount > 0) {
        return `${this.failureCount} failed`;
      }

      return '';
    },
    labelsStatusesWatchId() {
      return this.$store.state.mobileApp.tLabels.labelsStatusesWatchId;
    },
    actionButton() {
      if (this.allComplete || this.detectedLabelsCount === 0) {
        return {
          text: 'Close',
          action: this.clickClose,
        };
      }

      if (!this.isInProgress) {
        return {
          text: 'Start Download',
          action: this.startDownload,
        };
      }

      return {
        text: 'Cancel',
        action: this.showCancelDialog,
        isText: true,
      };
    },
    cleanedUpLabelsCount() {
      return this.$store.state.mobileApp.tLabels.cleanUpLabelsCount;
    },
    cleanedUpLabelsMessage() {
      if (this.cleanedUpLabelsCount === 0) {
        return '';
      }
      const isPlural = this.cleanedUpLabelsCount > 1;
      return `${this.cleanedUpLabelsCount} label${isPlural ? 's' : ''} recently downloaded ${isPlural ? 'are' : 'is'} not shown`;
    },
  },
  created() {
    if (!this.$root.workspaceFeatures.multiLabelTemperatureDownload) {
      this.$router.replace({ name: routeNames.home });
    } else if (!this.$store.getters.isMobileApp) {
      this.$router.replace({ name: routeNames.temperature.downloadRequiresApp });
    }
  },
  beforeDestroy() {
    this.$store.commit('mobileApp/tLabels/RESET_CLEAN_UP_LABELS_COUNT');
  },
  methods: {
    clickClose() {
      this.$router.go(-1);
    },
    startDownload() {
      console.log('Starting temperature data download');
      this.$store.dispatch('mobileApp/tLabels/startBulkDownload');
    },
    showCancelDialog() {
      this.displayCancelDialog = true;
    },
    cancelDownload() {
      this.isCancellingDownload = true;
      this.$store.dispatch('mobileApp/tLabels/stopBulkDownload');
      // this.clickClose();
      this.closeCancelDialog();
      this.isCancellingDownload = false;
    },
    closeCancelDialog() {
      this.displayCancelDialog = false;
    },
    retryLabelDownload(bleId) {
      this.$store.dispatch('mobileApp/tLabels/retryLabelDownload', bleId);
    },
  },
};
</script>
<style lang="scss" scoped>
@import '@/styles/_breakpoints.scss';
@import '@/styles/_variables.scss';
.temperature-data-download {
  display: flex;
  flex-direction: column;
  text-align: center;
  height: calc(100% - 60px);
  margin: 0 -14px;
  background-color: $white;
  border-bottom: solid 1px $grey-1;

  @include mq($from: md) {
    margin: 0 -36px 36px;
  }
}
.content-container {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: 0 14px 14px;
  overflow-y: auto;

  @include mq($from: md) {
    padding: 0 36px 36px;
  }
}
.summary-container {
  background-color: $tealLighter;
  color: $black;
  font-size: 0.875rem;
  padding: 8px 20px 5px;
  border-bottom: solid 1px $gray3;

  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  .item {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
    &-title {
      margin-right: 5px;
      text-align: right;
      width: 100px;
      &.highlight {
        text-align: center;
        font-weight: bold;
        background-color: $yellowLighter;
        // border-radius: 8px;
        width: 120px;
      }
    }
    &-value {
      font-weight: bold;
    }
  }
}
.loader-wrapper {
  margin-top: 40px;
}
.message-container {
  margin-top: 40px;
  &.is-loading {
    margin-top: 10px;
  }
}
.labels-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding-top: 14px;
}
.cleanup-message {
  font-style: italic;
  margin-top: 20px;
}
</style>
